import React from "react"
import { injectIntl } from "react-intl"
import * as Yup from "yup"
import { makeStyles } from "@material-ui/core/styles"
import ArrowForwardIcon from "@material-ui/icons/ArrowForward"
import { Box, Card, Grid, Typography } from "@material-ui/core"
import { Ridecell } from "@ridecell/web-sdk"
import {
  FormikForm,
  FormikFormContainer,
  TextInputForm,
} from "@ridecell/web-components-material"

import useGlobalContext from "../../../utils/customHooks/useGlobalContext"
import LoadingButton from "../../../components/LoadingButton"
import messages from "./messages"

const alphaNumeric = /^[a-zA-Z0-9]+$/

const validationSchema = (
  formatMessage,
  enableExternalDriverId,
  privateProfile
) =>
  Yup.object().shape({
    street_number: Yup.string()
      .required(formatMessage(messages.addressLine1IsRequired))
      .nullable(),
    street: Yup.string()
      .required(formatMessage(messages.addressLine2IsRequired))
      .nullable(),
    city: Yup.string()
      .required(formatMessage(messages.cityIsRequired))
      .nullable(),
    state: Yup.string()
      .required(formatMessage(messages.stateIsRequired))
      .nullable(),
    postal_code: Yup.string()
      .required(formatMessage(messages.zipCodeIsRequired))
      .nullable(),
    external_driver_id:
      enableExternalDriverId &&
      privateProfile &&
      Yup.string()
        .required(formatMessage(messages.externalDriverIdRequired))
        .matches(
          alphaNumeric,
          formatMessage(messages.externalDriverIdShouldBeAlphaNumeric)
        )
        .nullable(),
  })

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
  card: {
    margin: "24px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    boxShadow:
      "0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12)",
    borderRadius: "4px",
  },
  agreementText: {
    fontSize: "12px",
    letterSpacing: "0.4px",
    color: "#616161",
  },
})

const SaveAddress = ({
  handleNext,
  createCustomer,
  intl: { formatMessage },
  ...props
}) => {
  const classes = useStyles()
  const {
    state: { customer, loading, tenantSettings },
    setLoading,
    setError,
  } = useGlobalContext()
  const enableExternalDriverId = tenantSettings.enableExternalDriverId
  const privateProfile = customer && customer.profile_type === "PERSONAL"

  const saveAddress = async (values) => {
    setLoading(true)
    try {
      await Ridecell.saveMailingAddress({
        customerId: customer.id,
        data: values,
      })
      handleNext()
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }
  return (
    <React.Fragment>
      <Grid item xs={12} md={12} lg={6}>
        <Card className={classes.card}>
          <Box display="flex" sx={{ mb: "16px" }} justifyContent="center">
            <Typography>
              {formatMessage(messages.pleaseEnterYourAddress)}
            </Typography>
          </Box>
          <FormikFormContainer
            initialValues={{
              street_number: "",
              street: "",
              city: "",
              state: "",
              postal_code: "",
              ...(enableExternalDriverId &&
                privateProfile && {
                  external_driver_id:
                    (customer && customer.external_driver_id) || "",
                }),
            }}
            onSubmit={async (values) => {
              const postData = {
                mailing_address_display: `${values.street_number} ${values.street} ${values.city} ${values.postal_code} ${values.state}`,
                ...(enableExternalDriverId && {
                  ...values,
                }),
              }
              saveAddress(postData)
            }}
            validationSchema={validationSchema(
              formatMessage,
              enableExternalDriverId,
              privateProfile
            )}
          >
            {({ isValid, values }) => (
              <FormikForm>
                {enableExternalDriverId && privateProfile && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    sx={{ mb: "16px" }}
                  >
                    <TextInputForm
                      color="secondary"
                      label={formatMessage(messages.externalDriverId)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        maxLength: 64,
                      }}
                      name="external_driver_id"
                      required
                      variant="standard"
                      value={values.external_driver_id}
                    />
                  </Box>
                )}
                <Box display="flex" justifyContent="center" sx={{ mb: "16px" }}>
                  <TextInputForm
                    color="secondary"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label={formatMessage(messages.addressLine1)}
                    name="street_number"
                    required
                    value={values.street_number}
                  />
                </Box>
                <Box display="flex" justifyContent="center" sx={{ mb: "16px" }}>
                  <TextInputForm
                    color="secondary"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label={formatMessage(messages.addressLine2)}
                    name="street"
                    required
                    value={values.street}
                  />
                </Box>
                <Box display="flex" justifyContent="center" sx={{ mb: "16px" }}>
                  <TextInputForm
                    color="secondary"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label={formatMessage(messages.city)}
                    name="city"
                    required
                    value={values.city}
                  />
                </Box>
                <Box display="flex" justifyContent="center" sx={{ mb: "16px" }}>
                  <TextInputForm
                    color="state"
                    label={formatMessage(messages.state)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    name="state"
                    variant="standard"
                    required
                    value={values.state}
                  />
                </Box>
                <Box display="flex" justifyContent="center" sx={{ mb: "16px" }}>
                  <TextInputForm
                    color="secondary"
                    label={formatMessage(messages.zipCode)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    name="postal_code"
                    variant="standard"
                    required
                    value={values.postal_code}
                  />
                </Box>
                <LoadingButton
                  disabled={!isValid}
                  text={formatMessage(messages.next)}
                  variant="contained"
                  loading={loading}
                  fullWidth
                  type="submit"
                  props={{
                    fullWidth: true,
                    endIcon: <ArrowForwardIcon />,
                    size: "large",
                  }}
                />
              </FormikForm>
            )}
          </FormikFormContainer>
        </Card>
      </Grid>
    </React.Fragment>
  )
}

export default injectIntl(SaveAddress)
